import { createTheme } from "@mui/material";
import { USER_ROLES } from "constants/userRoles";

export const mainTheme = createTheme({
  status: {
    danger: "orange",
    error: "red",
    success: "green",
  },
  color: {
    secondary: "rgb(2, 76, 173)",
    primary100: "rgba(7, 65, 139, 1)",
    primary90: "rgba(80, 133, 224, 1)",
    primary60: "rgba(150, 182, 237, 1)",
    primary30: "rgba(223, 233, 250, 1)",
    accent100: "rgba(234,142,4,1)",
    accent90: "rgba(234,142,4,.9)",
    accent60: "rgba(234,142,4,.6)",
    accent30: "rgba(234,142,4,.3)",
    black100: "rgba(27, 29, 33, 1)",
    black90: "rgba(77, 77, 77, 1)",
    black60: "rgba(129, 129, 129, 1)",
    black30: "rgba(230, 230, 230, 1)",
    black20: "rgba(0,0,0,0.2)",
    black15: "rgba(0,0,0,0.15)",
    black10: "rgba(0,0,0,0.1)",
    white100: "rgba(255,255,255,1)",
    error90: "#E94040",
    error60: "#F18080",
    error30: "#F8BFBF",
    green: "#199716",

    buttonColorMain: "#148780",
    buttonColorSecondary: "#F7F7FC",

    text: "rgb(45,45,45)",
    textSecondary: "rgb(77,77,77)",
    selected: "rgba(228,234,255,1)",
    hovered: "rgba(228,234,255,0.6)",

    sidebarTitle: "#04303B",
  },
  backgroundColor: {
    white: "rgb(255,255,255)",
    white2: "#f6f6f6",
    notSelected: "#969696",
    grey: "#e3e3e3",
    primary30: "rgba(223, 233, 250, 0.3)",
    primary10: "rgba(223, 233, 250, 0.15)",
    black: "rgb(0,0,0)",
  },
  boxShadow: {
    right: "6px 0px 19px -7px rgba(0,0,0,0.2)",
    left: "-6px 0px 19px -7px rgba(0,0,0,0.2)",
    bottom: "0 8px 10px -10px rgba(0,0,0,0.6)",
    around: "0px 0px 10px 0px rgba(0,0,0,0.1)",
    aroundDark: "0px 0px 10px 0px rgba(0,0,0,0.3)",
    top: "0px 10px 10px 10px rgba(0,0,0,0.2)",
  },
  border: {
    notification: "1px solid #E5E5E5",
    weatherTabs: "1px solid rgba(230,230,230,1)",
  },
  margin: {
    default: "48px",
    sidebarOpen: "19rem",
    isMap: "20.625rem",
    fieldNav: "110px",
  },
  zIndex: {
    max: "999",
    navbar: "1014",
    drawer: "1013",
    drawerBackground: "1012",
    sidebar: "1011",
    fieldNav: "100",
    content: "0",
    editorDrawer: "1100",
    chatbot: "2000",
  },
  width: {
    navbar: "56px",
    drawer: "364px",
    sidebar: "19rem",
    sidebarFields: "19rem",
    actionsbar: "23.75rem",
  },
  height: {
    navbarTop: "48px",
    fieldNavBarDetails: "85px",
  },
  mobile: {
    margin: {
      sidebarOpen: "0",
      default: "0",
      marginBottomDefault: "48px",
    },
    height: {
      fieldNavBarDetails: "60px",
    },
  },
  navbarColors: {
    background: "#04303B",
    iconDefault: "#d9d9d9",
    [USER_ROLES.FARMER]: "#98C663",
    [USER_ROLES.AGRONOMIST]: "#2AC5EC",
    inboxBackground: "#EC9D3E",
    hovered: "#D9B44A",
  },
  components: {
    MuiButton: {
      defaultProps: {
        variant: "outlined",
        loadingPosition: "end",
      },
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: "14px",
          borderRadius: "1rem",
          variants: [
            {
              props: (props) => props.className.includes("view-mode"),
              style: {
                "&.Mui-disabled": {
                  color: "unset",
                },
              },
            },
            {
              props: (props) => props.className.includes("square"),
              style: {
                borderRadius: "0.25rem",
              },
            },
          ],
        },
        input: {
          padding: "5px 14px",
          variants: [
            {
              props: (props) => props.className.includes("view-mode"),
              style: {
                "&.Mui-disabled": {
                  "-webkit-text-fill-color": "unset",
                },
              },
            },
          ],
        },
        notchedOutline: {
          variants: [
            {
              props: (props) => props.className.includes("view-mode"),
              style: {
                borderWidth: 0,
              },
            },
          ],
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiFormLabel-root": {
            position: "inherit",
            marginBottom: "0.5rem",
            marginLeft: "-0.5rem",
            fontSize: "12px",
            fontWeight: 400,
          },
          "& .MuiInputBase-multiline": {
            padding: "0.5rem 0px",
          },
          "& .MuiInputAdornment-root": {
            marginLeft: 0,
          },
          "& .MuiFormHelperText-root": {
            marginLeft: "0.25rem",
            marginRight: "0px",
          },
          variants: [
            {
              props: (props) => props.className?.includes("view-mode"),
              style: {
                "& .MuiFormLabel-root.Mui-disabled": {
                  color: "rgba(0,0,0,.6)",
                },
              },
            },
          ],
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
            paddingTop: "5px",
            paddingBottom: "5px",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          fontSize: "14px",
          borderRadius: "1rem",
          "&.Mui-disabled": {
            backgroundColor: "#f0f0f0",
          },
          "&&& .MuiSelect-select": {
            paddingRight: "0.5rem",
          },
          variants: [
            {
              props: (props) => props.className?.includes("view-mode"),
              style: {
                "&.Mui-disabled": {
                  color: "unset",
                  backgroundColor: "unset",
                },
                "& .MuiSelect-outlined": {
                  paddingRight: 0,
                  "-webkit-text-fill-color": "unset",
                },
                "& .MuiSelect-icon": {
                  display: "none",
                },
              },
            },
            {
              props: (props) => props.className?.includes("square"),
              style: {
                borderRadius: "0.25rem",
              },
            },
          ],
        },
        icon: {
          zIndex: 10,
        },
      },
    },
  },
});
